import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { useTeamData } from './hooks/useTeamData';
import { UpgradePanel } from './components/UpgradePanel/UpgradePanel';
import { TeamHeader } from './components/TeamHeader/TeamHeader';
import { MemberList } from './components/MemberList/MemberList';
import { InviteModal } from './components/InviteModal/InviteModal';
import { UpgradeModal } from './components/UpgradeModal/UpgradeModal';
import { SUBSCRIPTION_PLANS } from '../../config/subscriptionPlans';
import './TeamManagement.css';

export function TeamManagement() {
  const [email, setEmail] = useState('');
  const [search, setSearch] = useState('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const {
    adminUser,
    maxMembers,
    members,
    isLoading,
    refetch,
    inviteAdmin,
    isInviting,
    removeAdmin,
    updatePlan,
  } = useTeamData();

  const isFreemium = adminUser?.plan === 'freemium' || !adminUser?.plan;

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    try {
      const res = await inviteAdmin({ email }).unwrap();

      Swal.fire({
        title: 'Invitation Sent!',
        html: `
          <div style="font-size: 14px; text-align: center;">
            <p>${res.message || 'A professional invitation email has been sent.'}</p>
            <p style="margin-top: 12px; font-size: 13px; color: #64748b;">
              When they accept, they will be able to create their account under your organization,
              customize their shark, and access the workspace.
            </p>
          </div>
        `,
        icon: 'success',
        confirmButtonColor: '#38bdf8',
      });

      refetch();
      setEmail('');
      setIsInviteModalOpen(false);
    } catch (err: any) {
      Swal.fire({
        title: 'Invitation Failed',
        text: err?.data?.message || 'There was an issue sending the invitation.',
        icon: 'error',
        confirmButtonColor: '#EF4444',
      });
    }
  };

  const handleRemove = (id: number, memberEmail: string, role: string) => {
    if (role === 'Admin') {
      Swal.fire({
        title: 'Action Denied',
        text: 'You cannot remove the primary administrator account.',
        icon: 'error',
        confirmButtonColor: '#EF4444',
      });
      return;
    }

    Swal.fire({
      title: 'Remove Member?',
      text: `Are you sure you want to remove ${memberEmail} from the workspace?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, remove',
      cancelButtonText: 'Cancel',
      customClass: { confirmButton: 'swal-danger' },
    }).then((result) => {
      if (result.isConfirmed) {
        removeAdmin(id.toString())
          .unwrap()
          .then(() => {
            Swal.fire({
              title: 'Removed!',
              text: `${memberEmail} has been removed.`,
              icon: 'success',
              confirmButtonColor: '#10B981',
            });
            refetch();
          })
          .catch((err: any) => {
            Swal.fire({
              title: 'Error',
              text: err?.data?.message || 'Failed to remove member.',
              icon: 'error',
              confirmButtonColor: '#EF4444',
            });
          });
      }
    });
  };

  const filteredMembers = members.filter(
    (m) => !search || m.email.toLowerCase().includes(search.toLowerCase())
  );

  const planLabel =
    adminUser?.plan === 'freemium' ? 'Free plan' : `${adminUser?.plan ?? 'current'} plan`;

  return (
    <div className="team-page">
      {/* Page heading */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h1 className="team-page__title">Team Management</h1>
          <p className="team-page__subtitle">
            Invite sales reps and manage workspace access.
          </p>
        </div>
        
        {/* Right section stats */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '40px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ color: 'var(--admin-card-subtext)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>
              Members Used
            </span>
            <span style={{ color: 'var(--admin-card-text)', fontSize: '24px', fontWeight: 800 }}>
              {members.length}
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ color: 'var(--admin-card-subtext)', fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>
              Seats Left
            </span>
            <span style={{ color: 'var(--admin-card-text)', fontSize: '24px', fontWeight: 800 }}>
              {maxMembers - members.length}
            </span>
          </div>
        </div>
      </div>

      {isFreemium ? (
        /* ── Freemium: upsell panel ─────────────────────────────── */
        <UpgradePanel
          planLabel={planLabel}
          onUpgrade={() => setIsUpgradeModalOpen(true)}
        />
      ) : (
        /* ── Paid plan: member management ──────────────────────── */
        <>
          <TeamHeader
            memberCount={members.length}
            maxMembers={maxMembers}
            search={search}
            showUpgradeBtn={adminUser?.plan !== 'team'}
            onSearchChange={setSearch}
            onInvite={() => setIsInviteModalOpen(true)}
            onUpgrade={() => setIsUpgradeModalOpen(true)}
          />

          <MemberList
            members={filteredMembers}
            isLoading={isLoading}
            currentUserEmail={adminUser?.email ?? ''}
            onRemove={handleRemove}
          />
        </>
      )}

      {/* Modals */}
      <InviteModal
        isOpen={isInviteModalOpen}
        email={email}
        isInviting={isInviting}
        onClose={() => setIsInviteModalOpen(false)}
        onEmailChange={setEmail}
        onSubmit={handleInvite}
      />

      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
      />
    </div>
  );
}
